import {
    THEME_GROUP_MESSAGE,
    THEME_INSPECT_MESSAGE,
    THEME_MODULE_MESSAGE,
    THEME_SELECT_MESSAGE,
    THEME_SELECTION_MESSAGE,
    THEME_HIGHLIGHT_MESSAGE,
} from '../../theme/tokens.js';
import {
    BLOCK_EDIT_MESSAGE,
    BLOCK_INSERT_MESSAGE,
    BLOCK_SELECT_MESSAGE,
    BLOCK_SPACE_MESSAGE,
} from '../../blocks/preview.js';
import { SLIDESHOW_VIEWS, normalizeExperienceOverride } from '../../slideshow/experience.js';
import { validFlashSpec } from '../../preview/colorFlash.js';

/** Editor to frame: group or ungroup the selected modules (the frame measures them). */
export const MODULE_GROUP_MESSAGE = 'portfolio:module-group';

/** Editor to frame: which device input and slide the slideshow preview shows. */
export const SLIDESHOW_STATE_MESSAGE = 'portfolio:slideshow-state';

/** Frame to editor: the empty slideshow's "Add images" was clicked. */
export const SLIDESHOW_ADD_IMAGES_MESSAGE = 'portfolio:slideshow-add-images';

/** Frame to editor: undo or redo was pressed while the preview held the keyboard. */
export const HISTORY_MESSAGE = 'portfolio:history';

export function historyCommand(action) {
    return { type: HISTORY_MESSAGE, action: action === 'redo' ? 'redo' : 'undo' };
}

export function slideshowState({ touch = null, view = 'auto', imageId = null } = {}) {
    return {
        type: SLIDESHOW_STATE_MESSAGE,
        touch: typeof touch === 'boolean' ? touch : null,
        view: SLIDESHOW_VIEWS.includes(view) ? view : 'auto',
        imageId: imageId || null,
    };
}

export function moduleGroupCommand(action) {
    return { type: MODULE_GROUP_MESSAGE, action: action === 'ungroup' ? 'ungroup' : 'group' };
}

export function selectModule(moduleId, moduleIds) {
    const message = {
        type: THEME_SELECT_MESSAGE,
        kind: 'module',
        module: moduleId || '',
        path: moduleId ? `module:${moduleId}` : 'module:',
    };
    if (Array.isArray(moduleIds)) message.moduleIds = moduleIds;
    return message;
}

export function selectPage({ path, id = '', blockId = '' }) {
    return {
        type: THEME_SELECT_MESSAGE,
        kind: 'page',
        path,
        id,
        blockId,
    };
}

export function selectTheme(path) {
    return {
        type: THEME_SELECT_MESSAGE,
        kind: 'theme',
        path,
    };
}

export function blockSelect({ blockId = '', tileId = '', tileKind = '' }) {
    return {
        type: BLOCK_SELECT_MESSAGE,
        blockId,
        tileId,
        tileKind,
    };
}

export function blockEdit(blockId) {
    return {
        type: BLOCK_EDIT_MESSAGE,
        blockId: blockId || '',
    };
}

export function blockInsert({ blockType, layout = '', afterId = '' }) {
    return {
        type: BLOCK_INSERT_MESSAGE,
        blockType,
        layout,
        afterId,
    };
}

export function blockSpace({ blockId, edge, stored, commit = false, begin = false }) {
    return {
        type: BLOCK_SPACE_MESSAGE,
        blockId,
        commit: Boolean(commit),
        begin: Boolean(begin),
        ...(edge === 'top' ? { marginTop: stored ?? 0 } : { marginBottom: stored ?? 0 }),
    };
}

export function moduleCommit({ id, breakpoint, mode, box, companions }) {
    const message = {
        type: THEME_MODULE_MESSAGE,
        id,
        breakpoint,
        mode,
        box,
    };
    if (Array.isArray(companions) && companions.length) message.companions = companions;
    return message;
}

export function moduleSelection(moduleId, moduleIds) {
    const message = {
        type: THEME_SELECTION_MESSAGE,
        moduleId: moduleId || '',
    };
    if (Array.isArray(moduleIds)) message.moduleIds = moduleIds;
    return message;
}

export function parseFrameMessage(data) {
    if (!data || typeof data !== 'object') {
        return { ok: false, reason: 'not an object' };
    }
    if (data.type === THEME_SELECT_MESSAGE) {
        if (data.kind === 'module') {
            const ids = data.moduleIds;
            const hasIds = Array.isArray(ids);
            if (typeof data.module !== 'string') {
                return { ok: false, reason: 'module select needs module id' };
            }
            if (!data.module && !hasIds) {
                return { ok: false, reason: 'module select needs module id' };
            }
            if (hasIds && ids.some((id) => typeof id !== 'string' || !id)) {
                return { ok: false, reason: 'moduleIds must be strings' };
            }
            return { ok: true, message: data };
        }
        if (typeof data.path !== 'string') {
            return { ok: false, reason: 'select needs path' };
        }
        return { ok: true, message: data };
    }
    if (data.type === THEME_MODULE_MESSAGE) {
        if (typeof data.id !== 'string' || !data.id) {
            return { ok: false, reason: 'module commit needs id' };
        }
        if (!data.box || typeof data.box !== 'object' || Array.isArray(data.box)) {
            return { ok: false, reason: 'module commit needs box object' };
        }
        if (data.mode !== 'move' && data.mode !== 'resize') {
            return { ok: false, reason: 'module commit mode' };
        }
        if (data.companions != null) {
            if (!Array.isArray(data.companions) || data.companions.some((item) => (
                !item || typeof item.id !== 'string' || !item.id
                || !item.box || typeof item.box !== 'object' || Array.isArray(item.box)
            ))) {
                return { ok: false, reason: 'companions need id and box' };
            }
        }
        return { ok: true, message: data };
    }
    if (data.type === BLOCK_SELECT_MESSAGE || data.type === BLOCK_EDIT_MESSAGE) {
        if (data.blockId != null && typeof data.blockId !== 'string') {
            return { ok: false, reason: 'blockId must be string' };
        }
        return { ok: true, message: data };
    }
    if (data.type === BLOCK_INSERT_MESSAGE) {
        if (typeof data.blockType !== 'string' || !data.blockType) {
            return { ok: false, reason: 'insert needs blockType' };
        }
        return { ok: true, message: data };
    }
    if (data.type === BLOCK_SPACE_MESSAGE) {
        if (typeof data.blockId !== 'string' || !data.blockId) {
            return { ok: false, reason: 'space needs blockId' };
        }
        return { ok: true, message: data };
    }
    if (data.type === HISTORY_MESSAGE) {
        if (data.action !== 'undo' && data.action !== 'redo') {
            return { ok: false, reason: 'history action' };
        }
        return { ok: true, message: data };
    }
    return { ok: true, message: data };
}

export function parseEditorMessage(data) {
    if (!data || typeof data !== 'object') {
        return { ok: false, reason: 'not an object' };
    }
    if (data.type === THEME_GROUP_MESSAGE) {
        if (data.group != null && typeof data.group !== 'string') {
            return { ok: false, reason: 'group must be string' };
        }
        return { ok: true, message: data };
    }
    if (data.type === THEME_INSPECT_MESSAGE) {
        return { ok: true, message: { ...data, enabled: Boolean(data.enabled) } };
    }
    if (data.type === THEME_HIGHLIGHT_MESSAGE) {
        if (data.selectors != null && (
            !Array.isArray(data.selectors)
            || data.selectors.some((sel) => typeof sel !== 'string' || sel.length > 200)
        )) {
            return { ok: false, reason: 'highlight selectors' };
        }
        if (data.flash != null && !validFlashSpec(data.flash)) {
            return { ok: false, reason: 'highlight flash' };
        }
        return { ok: true, message: data };
    }
    if (data.type === THEME_SELECTION_MESSAGE) {
        if (typeof data.moduleId !== 'string') {
            return { ok: false, reason: 'moduleId must be string' };
        }
        if (data.moduleIds != null && (
            !Array.isArray(data.moduleIds)
            || data.moduleIds.some((id) => typeof id !== 'string')
        )) {
            return { ok: false, reason: 'moduleIds must be strings' };
        }
        return { ok: true, message: data };
    }
    if (data.type === MODULE_GROUP_MESSAGE) {
        if (data.action !== 'group' && data.action !== 'ungroup') {
            return { ok: false, reason: 'group action' };
        }
        return { ok: true, message: data };
    }
    if (data.type === SLIDESHOW_STATE_MESSAGE) {
        if (data.touch != null && typeof data.touch !== 'boolean') {
            return { ok: false, reason: 'touch must be boolean or null' };
        }
        if (data.view != null && !SLIDESHOW_VIEWS.includes(data.view)) {
            return { ok: false, reason: 'unknown slideshow view' };
        }
        if (data.imageId != null && (typeof data.imageId !== 'string' || data.imageId.length > 120)) {
            return { ok: false, reason: 'imageId must be string' };
        }
        const state = normalizeExperienceOverride(data);
        return {
            ok: true,
            message: { type: SLIDESHOW_STATE_MESSAGE, touch: state?.touch ?? null, view: state?.view || 'auto', imageId: state?.imageId || null },
        };
    }
    if (data.type === BLOCK_SELECT_MESSAGE || data.type === BLOCK_EDIT_MESSAGE) {
        const blockId = data.blockId == null ? '' : data.blockId;
        if (typeof blockId !== 'string') {
            return { ok: false, reason: 'blockId must be string' };
        }
        return { ok: true, message: { ...data, blockId } };
    }
    return { ok: true, message: data };
}
